import React from 'react';

/**
 * Deterministic, on-brand SVG cover art for an article.
 * Topic -> gradient + science-motif icon; the article id seeds a generative
 * pattern so covers within a topic still differ. No external images.
 */

const ICONS = {
    brain: <><path d="M9 3a3 3 0 0 0-3 3 3 3 0 0 0-1 5 3 3 0 0 0 1 5 3 3 0 0 0 3 3 3 3 0 0 0 3-1V4a3 3 0 0 0-3-1Z"/><path d="M15 3a3 3 0 0 1 3 3 3 3 0 0 1 1 5 3 3 0 0 1-1 5 3 3 0 0 1-3 3 3 3 0 0 1-3-1"/><path d="M9 8h1M14 8h1M9 12h1M14 12h1M9 16h1"/></>,
    dna: <><path d="M7 3c0 5 10 5 10 10s-10 5-10 10M17 3c0 5-10 5-10 10s10 5 10 10"/><path d="M8.5 6h7M8.5 18h7M7 9.5h10M7 14.5h10"/></>,
    leaf: <><path d="M4 20c0-9 7-16 16-16 0 9-7 16-16 16Z"/><path d="M4 20C8 14 13 10 18 8"/></>,
    droplet: <path d="M12 3s7 7 7 12a7 7 0 0 1-14 0c0-5 7-12 7-12Z"/>,
    heart: <><path d="M12 20s-7-4.5-7-9.5A4 4 0 0 1 12 7a4 4 0 0 1 7 3.5C19 15.5 12 20 12 20Z"/><path d="M6 12h3l1.5-3L13 15l1.5-3H18"/></>,
    clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    moon: <path d="M20 14A8 8 0 0 1 10 4a8 8 0 1 0 10 10Z"/>,
    microbe: <><circle cx="12" cy="12" r="7"/><path d="M12 5V2M12 22v-3M5 12H2M22 12h-3M6.8 6.8 4.7 4.7M19.3 19.3l-2.1-2.1M17.2 6.8l2.1-2.1M4.7 19.3l2.1-2.1"/><circle cx="10" cy="11" r="1.3"/><circle cx="14.5" cy="14" r="1.1"/></>,
    target: <><circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="4.5"/><circle cx="12" cy="12" r="1"/></>,
    dumbbell: <><path d="M6.5 6.5v11M17.5 6.5v11M4 9v6M20 9v6M6.5 12h11"/></>,
    pill: <><rect x="3" y="8" width="18" height="8" rx="4"/><path d="M12 8v8"/></>,
    spiral: <path d="M12 12a2 2 0 1 1 2 2 4 4 0 0 1-4-4 6 6 0 0 1 6-6 8 8 0 0 1 8 8"/>,
    sprout: <><path d="M12 20v-8"/><path d="M12 12C12 8 9 5 4 5c0 4 3 7 8 7Z"/><path d="M12 13c0-3 3-6 8-6 0 4-3 7-8 7Z"/></>,
    bolt: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z"/>,
    watch: <><rect x="7" y="7" width="10" height="10" rx="3"/><path d="M9 7 8.5 3h7L15 7M9 17l-.5 4h7l-.5-4M12 10v2.5l1.5 1"/></>,
    hourglass: <><path d="M6 3h12M6 21h12"/><path d="M7 3c0 5 5 6 5 9s-5 4-5 9M17 3c0 5-5 6-5 9s5 4 5 9"/></>,
    vesicle: <><circle cx="12" cy="12" r="8"/><circle cx="10" cy="10" r="2"/><circle cx="15" cy="13" r="1.4"/></>,
    infinity: <path d="M6 12a3 3 0 1 1 3 3c-2 0-3-3-6-3M18 12a3 3 0 1 0-3 3c2 0 3-3 6-3"/>,
    plate: <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/></>,
    lotus: <><path d="M12 20c-5 0-8-3-8-3s2-5 8-5 8 5 8 5-3 3-8 3Z"/><path d="M12 12c-2-2-2-6 0-8 2 2 2 6 0 8Z"/></>,
    link: <><circle cx="8" cy="8" r="4"/><circle cx="16" cy="16" r="4"/><path d="M10.5 10.5l3 3"/></>,
    atom: <><circle cx="12" cy="12" r="2"/><ellipse cx="12" cy="12" rx="10" ry="4"/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)"/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)"/></>,
};

const TOPIC = {
    'AI-Driven Diagnostics': ['#0e7490', '#0ea5e9', 'brain'],
    'AI-Powered Meal Planning': ['#0f766e', '#22c55e', 'plate'],
    'Biological Age': ['#155e75', '#14b8a6', 'hourglass'],
    'Chronotherapy': ['#3730a3', '#6366f1', 'clock'],
    'Exosome Therapy': ['#0369a1', '#06b6d4', 'vesicle'],
    'Healthspan': ['#047857', '#10b981', 'heart'],
    'Herbal Therapy': ['#15803d', '#65a30d', 'leaf'],
    'Holistic Health': ['#0d9488', '#34d399', 'lotus'],
    'Liquid Biopsies': ['#9f1239', '#e11d48', 'droplet'],
    'Longevity Medicine': ['#065f46', '#2dd4bf', 'infinity'],
    'Mental Fitness': ['#6d28d9', '#8b5cf6', 'brain'],
    'Microbiome': ['#a16207', '#eab308', 'microbe'],
    'Nutrigenomics': ['#7c3aed', '#a855f7', 'dna'],
    'Personalized Cancer Therapies': ['#9d174d', '#db2777', 'target'],
    'Personalized Exercise Prescriptions': ['#0f766e', '#84cc16', 'dumbbell'],
    'Personalized Fasting': ['#1e40af', '#3b82f6', 'plate'],
    'Pharmacogenomics': ['#7e22ce', '#c026d3', 'pill'],
    'Polygenic Risk Scores': ['#5b21b6', '#7c3aed', 'dna'],
    'Precision Sleep Medicine': ['#1e3a8a', '#4f46e5', 'moon'],
    'Psychedelic Wellness': ['#7c3aed', '#ec4899', 'spiral'],
    'Regenerative Nutrition': ['#3f6212', '#16a34a', 'sprout'],
    'Stress Biology': ['#b45309', '#f59e0b', 'bolt'],
    'The Gut-Brain-Skin Axis': ['#0e7490', '#8b5cf6', 'link'],
    'Wearables and Health Trackers': ['#115e59', '#2dd4bf', 'watch'],
};
const DEFAULT = ['#0d9488', '#34e393', 'atom'];

const hash = (str) => {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
    return h >>> 0;
};
const makeRng = (seed) => () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
};

const ArticleCover = ({topic, seed, className = ''}) => {
    const [c1, c2, iconKey] = TOPIC[topic] || DEFAULT;
    const h = hash(String(seed || topic));
    const rand = makeRng(h);
    const gid = `cov-${h.toString(36)}`;

    // seeded decorative shapes for a molecular / abstract feel
    const rings = Array.from({length: 5}, () => ({
        cx: Math.round(rand() * 400),
        cy: Math.round(rand() * 225),
        r: Math.round(18 + rand() * 66),
        o: (0.05 + rand() * 0.1).toFixed(3),
        f: rand() > 0.55,
    }));
    // a couple of connector lines
    const lines = Array.from({length: 2}, () => ({
        x1: Math.round(rand() * 400), y1: Math.round(rand() * 225),
        x2: Math.round(rand() * 400), y2: Math.round(rand() * 225),
    }));
    const iconX = 232 + Math.round(rand() * 40);
    const iconY = 44 + Math.round(rand() * 30);
    const iconR = -12 + Math.round(rand() * 24);

    return (
        <svg className={`ArtCover ${className}`} viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice"
             role="img" aria-label={`${topic} cover art`}>
            <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor={c1}/>
                    <stop offset="1" stopColor={c2}/>
                </linearGradient>
            </defs>
            <rect width="400" height="225" fill={`url(#${gid})`}/>
            <g stroke="#ffffff" fill="none">
                {lines.map((l, i) => <line key={`l${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} strokeOpacity="0.10" strokeWidth="1.5"/>)}
            </g>
            {rings.map((s, i) => (
                <circle key={`c${i}`} cx={s.cx} cy={s.cy} r={s.r}
                        fill={s.f ? '#ffffff' : 'none'} fillOpacity={s.f ? s.o : 0}
                        stroke={s.f ? 'none' : '#ffffff'} strokeOpacity={s.o} strokeWidth="1.6"/>
            ))}
            <rect width="400" height="225" fill="#000000" fillOpacity="0.06"/>
            {/* topic motif */}
            <svg x={iconX} y={iconY} width="132" height="132" viewBox="0 0 24 24"
                 fill="none" stroke="#ffffff" strokeOpacity="0.9" strokeWidth="1.4"
                 strokeLinecap="round" strokeLinejoin="round"
                 style={{transform: `rotate(${iconR}deg)`, transformOrigin: '298px 110px'}}>
                {ICONS[iconKey] || ICONS.atom}
            </svg>
        </svg>
    );
};

export default ArticleCover;
